import { formatDate } from "../Experiment/RolloutDetails/rolloutDetails.utils";
import { styles } from "./shared.styles";
import { getStatusColor, normalizeStatus } from "./shared.utils";

/**
 * Renders a cell with "Not Set" text or a custom text if provided.
 * @param notSetText - Optional custom text to display instead of "Not Set".
 */
export function notSetCell(notSetText?: string | undefined | null) {
  const title = notSetText || "Not Set";
  return (
    <span className={styles.tableNotSet}>{accessibleTextRenderer(title)}</span>
  );
}

/**
 * Renders component with accessible text using the title attribute.
 * @param text Text to be displayed and set in title attribute.
 */
export function accessibleTextRenderer(text: string) {
  return (
    <span
      title={text}
      style={{
        fontSize: 13,
      }}
    >
      {text}
    </span>
  );
}

/**
 * Displays no items to display or a custom message if provided
 * @param text custom message to display
 */
export function noTableItemsRenderer(text?: string) {
  return (
    <span className={styles.noTableItems}>{text || "No items to display"}</span>
  );
}

/**
 * Displays a colored dot along with the normalized status text
 * @param status Status string to be normalized and displayed
 */
export function dotStatusRenderer(status: string) {
  const normalizedStatus = normalizeStatus(status);
  const statusColor = getStatusColor(normalizedStatus);
  return (
    <div className={styles.dotCellRendererContainer}>
      <div
        style={{ backgroundColor: statusColor }}
        className={styles.dotCellRendererDot}
      />
      <span
        style={{ color: statusColor }}
        className={styles.dotCellRendererText}
      >
        {accessibleTextRenderer(normalizedStatus)}
      </span>
    </div>
  );
}

/**
 * Displays multiple statuses with counts in a single cell
 * @param statuses Array of  [status, count] to be displayed
 */
export function multiStatusRenderer(statuses: [string, number][]) {
  return (
    <div className={styles.multiStatusCell}>
      {statuses.map(([status, count]) => {
        const statusColor = getStatusColor(status);
        return (
          <span
            key={status}
            style={{
              backgroundColor: `${statusColor}20`,
              color: statusColor,
              border: `1px solid ${statusColor}40`,
            }}
            className={styles.multiStatusCellStatus}
          >
            {accessibleTextRenderer(`${count} ${status}`)}
          </span>
        );
      })}
    </div>
  );
}

/**
 * Displays a loading indicator with optional label. Defaults to "Loading..."
 * @param label Optional label to display alongside loading indicator
 * @returns
 */
export function loadingRenderer(label?: string) {
  return (
    <span className={styles.colorUnknown}>
      {accessibleTextRenderer(label || "Loading...")}
    </span>
  );
}

/**
 * Displays a formatted date or "N/A" if dateString is not provided
 * @param dateString date to be displayed
 * @returns
 */
export function dateRenderer(dateString: string) {
  if (!dateString) return notSetCell("N/A");
  return formatDate(dateString);
}
